import { JSX, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";

import useFetch from "utils/fetch/useFetch";

import { useAuth } from "authentication/AuthProvider";
import SelectOrganisationForm from "authentication/SelectOrganisationForm";

import OrganisationClient from "organisation/OrganisationClient";
import { OrganisationOverview } from "organisation/OrganisationTypes";
import { getLogo } from "organisation/OrganisationUtils";

const DEFAULT_IMAGE: JSX.Element = (
	<img src="/logo/logo32.png" aria-label="organisation-logo" />
);

export default function OrganisationSelector(): JSX.Element {
	const navigate = useNavigate();
	const { principal, refreshTokens } = useAuth();
	const { call } = useFetch();

	const [organisations, setOrganisations] = useState<
		Array<OrganisationOverview>
	>([]);

	useEffect(() => {
		if (principal?.isFullyAuthenticated()) {
			call(OrganisationClient.getAll()).then(setOrganisations);
		}
	}, [principal]);

	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLElement>): void => {
			setAnchorEl(event.currentTarget);
		},
		[]
	);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	function getCurrentOrganisationLogo(): JSX.Element {
		if (!principal?.isFullyAuthenticated()) {
			return DEFAULT_IMAGE;
		}

		const orgId = principal?.organisationId;
		const organisation = organisations?.find(org => org.id === orgId);
		if (orgId === undefined || organisation === undefined) {
			return DEFAULT_IMAGE;
		}

		return getLogo(organisation);
	}

	const onCreateOrganisation = useCallback((): void => {
		setAnchorEl(null);
		navigate("/create-organisation");
	}, []);

	const switchOrganisation = useCallback(
		(id: number): void => {
			refreshTokens(id)
				.then(() => navigate("/"))
				.then(handleClose);
		},
		[refreshTokens]
	);

	return (
		<>
			<Button
				disabled={!principal?.isFullyAuthenticated()}
				onClick={handleClick}
				aria-label="organisation-select">
				{getCurrentOrganisationLogo()}
			</Button>
			<Popover
				open={anchorEl !== null}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left"
				}}>
				<SelectOrganisationForm
					organisations={organisations}
					onSelectOrganisation={switchOrganisation}
					onCreateOrganisation={onCreateOrganisation}
				/>
			</Popover>
		</>
	);
}
